import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { notifications } from '@mantine/notifications'
import { useTenantAtual, useTenantUpdate } from '../../hooks/useTenant'
import { useAuthStore } from '../../stores/authStore'
import MinhaEmpresaSection from './MinhaEmpresaSection'
import styles from './ConfiguracoesPage.module.css'

const UF_VALIDAS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
]

const TIPO_JURIDICO_OPTIONS = [
  { value: 'ltda', label: 'Sociedade Limitada' },
  { value: 'sa', label: 'Sociedade Anônima' },
  { value: 'eireli', label: 'EIRELI' },
  { value: 'mei', label: 'MEI' },
  { value: 'individual', label: 'Empresa Individual' },
  { value: 'outro', label: 'Outro' },
]

const tenantSchema = z.object({
  tipo_juridico: z.enum(['ltda', 'sa', 'eireli', 'mei', 'individual', 'outro']).default('ltda'),
  registro_crea_pj: z.string().max(20).default(''),
  uf_registro: z
    .string()
    .refine((v) => v === '' || UF_VALIDAS.includes(v.toUpperCase()), 'UF inválida')
    .transform((v) => v.toUpperCase())
    .default(''),
  email_contato: z
    .string()
    .refine((v) => v === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), 'E-mail inválido')
    .default(''),
  telefone: z
    .string()
    .refine((v) => v === '' || /^[\d\s()+-]{8,20}$/.test(v), 'Telefone inválido')
    .default(''),
})

type TenantFormData = z.infer<typeof tenantSchema>

type Tab = 'empresa' | 'minha-empresa' | 'usuarios' | 'integracoes'

export default function ConfiguracoesPage() {
  const [tab, setTab] = useState<Tab>('empresa')
  const { data: tenant, isLoading } = useTenantAtual()
  const update = useTenantUpdate()
  const perfil = useAuthStore((s) => s.user?.perfil)
  const isAdmin = perfil === 'admin_tenant'

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TenantFormData>({
    resolver: zodResolver(tenantSchema),
    defaultValues: {
      tipo_juridico: 'ltda',
      registro_crea_pj: '',
      uf_registro: '',
      email_contato: '',
      telefone: '',
    },
  })

  useEffect(() => {
    if (tenant) {
      reset({
        tipo_juridico: tenant.tipo_juridico ?? 'ltda',
        registro_crea_pj: tenant.registro_crea_pj ?? '',
        uf_registro: tenant.uf_registro ?? '',
        email_contato: tenant.email_contato ?? '',
        telefone: tenant.telefone ?? '',
      })
    }
  }, [tenant, reset])

  async function handleSave(data: TenantFormData) {
    try {
      await update.mutateAsync(data)
      notifications.show({ color: 'green', message: 'Dados da empresa atualizados.' })
    } catch {
      notifications.show({ color: 'red', message: 'Erro ao salvar. Tente novamente.' })
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Configurações</h1>
      </header>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${tab === 'empresa' ? styles.tabActive : ''}`}
          onClick={() => setTab('empresa')}
          type="button"
        >
          Empresa (jurídico)
        </button>
        <button
          className={`${styles.tab} ${tab === 'minha-empresa' ? styles.tabActive : ''}`}
          onClick={() => setTab('minha-empresa')}
          type="button"
          data-testid="tab-minha-empresa"
        >
          Minha Empresa (logo + papéis)
        </button>
        <button
          className={`${styles.tab} ${tab === 'usuarios' ? styles.tabActive : ''}`}
          onClick={() => setTab('usuarios')}
          type="button"
        >
          Usuários
        </button>
        <button
          className={`${styles.tab} ${tab === 'integracoes' ? styles.tabActive : ''}`}
          onClick={() => setTab('integracoes')}
          type="button"
        >
          Integrações
        </button>
      </div>

      {tab === 'empresa' && (
        <div className={styles.tabContent}>
          {isLoading ? (
            <p>Carregando...</p>
          ) : (
            <form onSubmit={handleSubmit(handleSave)} className={styles.form} noValidate>
              <div className={styles.grid2}>
                <div className={styles.field}>
                  <label htmlFor="razao_social_display">Razão Social</label>
                  <input
                    id="razao_social_display"
                    value={tenant?.nome ?? ''}
                    readOnly
                    disabled
                  />
                  <span className={styles.helper}>
                    Alterações na razão social requerem suporte.
                  </span>
                </div>
                <div className={styles.field}>
                  <label htmlFor="cnpj_display">CNPJ</label>
                  <input
                    id="cnpj_display"
                    value={tenant?.cnpj ?? '—'}
                    readOnly
                    disabled
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="tipo_juridico">Tipo Jurídico</label>
                  <select
                    id="tipo_juridico"
                    {...register('tipo_juridico')}
                    aria-invalid={!!errors.tipo_juridico}
                    aria-describedby={errors.tipo_juridico ? 'tipo_juridico-error' : undefined}
                    disabled={!isAdmin}
                  >
                    {TIPO_JURIDICO_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {errors.tipo_juridico && (
                    <span id="tipo_juridico-error" role="alert" className={styles.error}>{errors.tipo_juridico.message}</span>
                  )}
                </div>
                <div className={styles.field}>
                  <label htmlFor="registro_crea_pj">Registro CREA PJ</label>
                  <input
                    id="registro_crea_pj"
                    {...register('registro_crea_pj')}
                    placeholder="Ex: SP-123456"
                    disabled={!isAdmin}
                    readOnly={!isAdmin}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="uf_registro">UF do Registro CREA</label>
                  <select
                    id="uf_registro"
                    {...register('uf_registro')}
                    aria-invalid={!!errors.uf_registro}
                    aria-describedby={errors.uf_registro ? 'uf_registro-error' : undefined}
                    disabled={!isAdmin}
                  >
                    <option value="">— Selecionar —</option>
                    {UF_VALIDAS.map((uf) => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </select>
                  {errors.uf_registro && (
                    <span id="uf_registro-error" role="alert" className={styles.error}>{errors.uf_registro.message}</span>
                  )}
                </div>
                <div className={styles.field}>
                  <label htmlFor="email_contato">E-mail de contato</label>
                  <input
                    id="email_contato"
                    type="email"
                    {...register('email_contato')}
                    aria-invalid={!!errors.email_contato}
                    aria-describedby={errors.email_contato ? 'email_contato-error' : undefined}
                    placeholder="contato@empresa.com.br"
                    disabled={!isAdmin}
                    readOnly={!isAdmin}
                  />
                  {errors.email_contato && (
                    <span id="email_contato-error" role="alert" className={styles.error}>
                      {errors.email_contato.message}
                    </span>
                  )}
                  <span className={styles.helper}>
                    Usado para envio de notificações e comunicação oficial.
                  </span>
                </div>
                <div className={styles.field}>
                  <label htmlFor="telefone">Telefone</label>
                  <input
                    id="telefone"
                    {...register('telefone')}
                    aria-invalid={!!errors.telefone}
                    aria-describedby={errors.telefone ? 'telefone-error' : undefined}
                    placeholder="(11) 99999-9999"
                    disabled={!isAdmin}
                    readOnly={!isAdmin}
                  />
                  {errors.telefone && (
                    <span id="telefone-error" role="alert" className={styles.error}>
                      {errors.telefone.message}
                    </span>
                  )}
                </div>
              </div>

              <div
                style={{
                  marginTop: '1.5rem',
                  padding: '1rem',
                  background: 'var(--mantine-color-gray-0)',
                  borderRadius: 8,
                  fontSize: '0.875rem',
                }}
              >
                <strong>📄 Logo no PDF do PMOC</strong>
                <p style={{ margin: '0.5rem 0 0', color: 'var(--mantine-color-dimmed)' }}>
                  Logo, papéis e marca da sua empresa estão na aba{' '}
                  <strong>Minha Empresa</strong> ao lado. Logos de
                  clientes/executores parceiros são editáveis no{' '}
                  <a href="/empresas" style={{ textDecoration: 'underline' }}>
                    cadastro de Empresas
                  </a>
                  .
                </p>
              </div>

              {!isAdmin && (
                <p className={styles.helper}>
                  Apenas administradores podem editar dados da empresa.
                </p>
              )}

              {isAdmin && (
                <div className={styles.actions}>
                  <button type="submit" className={styles.btnPrimary} disabled={update.isPending}>
                    {update.isPending ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      )}

      {tab === 'minha-empresa' && (
        <div className={styles.tabContent}>
          <MinhaEmpresaSection isAdmin={isAdmin} />
        </div>
      )}

      {tab === 'usuarios' && (
        <div className={styles.tabContent}>
          <p>
            Gestão de membros da equipe (admin_tenant cria RTs e técnicos):
          </p>
          <p>
            <a
              href="/usuarios"
              style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: '#1565C0',
                color: 'white',
                textDecoration: 'none',
                borderRadius: 4,
                fontWeight: 600,
              }}
            >
              👥 Abrir gestão de usuários
            </a>
          </p>
        </div>
      )}

      {tab === 'integracoes' && (
        <div className={styles.tabContent}>
          <SMTPSection tenant={tenant} isAdmin={isAdmin} />
          <p className={styles.helper} style={{ marginTop: '2rem' }}>
            Outras integrações em breve — ZapSign (assinatura digital).
          </p>
        </div>
      )}
    </div>
  )
}

const smtpSchema = z.object({
  smtp_host: z.string().max(255).default(''),
  smtp_port: z
    .union([z.coerce.number().int().min(1).max(65535), z.literal('').transform(() => null)])
    .nullable()
    .optional(),
  smtp_usuario: z.string().max(255).default(''),
  smtp_senha: z.string().default(''),
  smtp_remetente: z
    .string()
    .refine((v) => v === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), 'E-mail inválido')
    .default(''),
  smtp_use_tls: z.boolean().default(true),
})

type SMTPFormData = z.infer<typeof smtpSchema>

function SMTPSection({
  tenant,
  isAdmin,
}: {
  tenant: ReturnType<typeof useTenantAtual>['data']
  isAdmin: boolean
}) {
  const update = useTenantUpdate()
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SMTPFormData>({
    resolver: zodResolver(smtpSchema),
    defaultValues: {
      smtp_host: '',
      smtp_port: null,
      smtp_usuario: '',
      smtp_senha: '',
      smtp_remetente: '',
      smtp_use_tls: true,
    },
  })

  useEffect(() => {
    if (tenant) {
      reset({
        smtp_host: tenant.smtp_host ?? '',
        smtp_port: tenant.smtp_port ?? null,
        smtp_usuario: tenant.smtp_usuario ?? '',
        smtp_senha: '',
        smtp_remetente: tenant.smtp_remetente ?? '',
        smtp_use_tls: tenant.smtp_use_tls ?? true,
      })
    }
  }, [tenant, reset])

  async function handleSave(data: SMTPFormData) {
    const payload: Partial<TenantFormData & SMTPFormData> = { ...data }
    // Não envia senha se o campo estiver vazio (mantém a atual no backend)
    if (!data.smtp_senha) delete (payload as Record<string, unknown>).smtp_senha
    try {
      await update.mutateAsync(payload as never)
      notifications.show({ color: 'green', message: 'SMTP atualizado.' })
    } catch {
      notifications.show({ color: 'red', message: 'Erro ao salvar SMTP.' })
    }
  }

  return (
    <form onSubmit={handleSubmit(handleSave)} className={styles.form} noValidate>
      <h2 style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>📧 SMTP — Envio de e-mails</h2>
      <p className={styles.helper} style={{ marginBottom: '1rem' }}>
        Configure o servidor SMTP para que o sistema envie notificações por email aos
        usuários. Sem isso, as notificações continuam aparecendo apenas in-app.
      </p>

      <div className={styles.grid2}>
        <div className={styles.field}>
          <label htmlFor="smtp_host">Servidor (host)</label>
          <input
            id="smtp_host"
            {...register('smtp_host')}
            placeholder="smtp.gmail.com"
            disabled={!isAdmin}
            readOnly={!isAdmin}
          />
        </div>
        <div className={styles.field}>
          <label htmlFor="smtp_port">Porta</label>
          <input
            id="smtp_port"
            type="number"
            {...register('smtp_port')}
            placeholder="587"
            disabled={!isAdmin}
            readOnly={!isAdmin}
          />
          {errors.smtp_port && (
            <span role="alert" className={styles.error}>
              {errors.smtp_port.message}
            </span>
          )}
        </div>
        <div className={styles.field}>
          <label htmlFor="smtp_usuario">Usuário</label>
          <input
            id="smtp_usuario"
            {...register('smtp_usuario')}
            placeholder="usuario@dominio.com"
            disabled={!isAdmin}
            readOnly={!isAdmin}
            autoComplete="off"
          />
        </div>
        <div className={styles.field}>
          <label htmlFor="smtp_senha">
            Senha {tenant?.smtp_senha_configurada ? '(deixe em branco para manter)' : ''}
          </label>
          <input
            id="smtp_senha"
            type="password"
            {...register('smtp_senha')}
            placeholder={tenant?.smtp_senha_configurada ? '••••••••' : 'senha SMTP'}
            disabled={!isAdmin}
            readOnly={!isAdmin}
            autoComplete="new-password"
          />
        </div>
        <div className={styles.field}>
          <label htmlFor="smtp_remetente">Remetente (From)</label>
          <input
            id="smtp_remetente"
            type="email"
            {...register('smtp_remetente')}
            placeholder="naoresponda@empresa.com.br"
            disabled={!isAdmin}
            readOnly={!isAdmin}
          />
          {errors.smtp_remetente && (
            <span role="alert" className={styles.error}>
              {errors.smtp_remetente.message}
            </span>
          )}
        </div>
        <div className={styles.field}>
          <label
            htmlFor="smtp_use_tls"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
          >
            <input
              id="smtp_use_tls"
              type="checkbox"
              {...register('smtp_use_tls')}
              disabled={!isAdmin}
            />
            Usar TLS (recomendado)
          </label>
        </div>
      </div>

      <div
        style={{
          marginTop: '1rem',
          padding: '0.75rem',
          background: tenant?.smtp_configurado
            ? 'var(--mantine-color-teal-0)'
            : 'var(--mantine-color-gray-0)',
          borderRadius: 8,
          fontSize: '0.875rem',
        }}
      >
        {tenant?.smtp_configurado ? (
          <>
            ✅ <strong>SMTP ativo</strong> — emails serão enviados quando preferências do
            usuário permitirem.
          </>
        ) : (
          <>
            ⚠️ <strong>SMTP incompleto</strong> — preencha host, porta, usuário, senha e
            remetente para ativar email.
          </>
        )}
      </div>

      {isAdmin && (
        <div className={styles.actions}>
          <button type="submit" className={styles.btnPrimary} disabled={update.isPending}>
            {update.isPending ? 'Salvando...' : 'Salvar SMTP'}
          </button>
        </div>
      )}
    </form>
  )
}
